// src/hooks/usePhotoAnalysis.ts
// Hook for managing photo analysis workflow: consent, capture, and AI analysis
// Extracted from App.js

import { useState, useCallback, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { doc, setDoc, increment } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../services/firebaseConfig';
import type { AnalysisProvider } from '../components/modals/FaceGuidelineModal';
import { DEFAULT_ANALYSIS_PROVIDER } from '../components/modals/FaceGuidelineModal';
import { getPermissionGuidanceKey, detectPlatform } from '../utils/platformDetection';

/**
 * Master profile with consent information
 */
interface MasterProfileConsent {
  photoAnalysisAccepted?: boolean;
  photoConsentTimestamp?: string;
}

interface MasterProfile {
  consent?: MasterProfileConsent;
  [key: string]: unknown;
}

/**
 * Hook options
 */
interface UsePhotoAnalysisOptions {
  /** Current master profile */
  masterProfile: MasterProfile;
  /** Function to update master profile */
  setMasterProfile: Dispatch<SetStateAction<MasterProfile>>;
}

/**
 * Return type for usePhotoAnalysis hook
 */
interface UsePhotoAnalysisReturn {
  // Photo state
  selectedImage: File | null;
  setSelectedImage: Dispatch<SetStateAction<File | null>>;
  imagePreviewUrl: string | null;
  setImagePreviewUrl: Dispatch<SetStateAction<string | null>>;
  shouldAutoSend: boolean;
  resetAutoSend: () => void;
  clearPhotoState: () => void;
  
  // Analysis provider
  analysisProvider: AnalysisProvider;
  setAnalysisProvider: Dispatch<SetStateAction<AnalysisProvider>>;

  // Modal state
  showPhotoConsent: boolean;
  setShowPhotoConsent: Dispatch<SetStateAction<boolean>>;
  showFaceGuideline: boolean;
  setShowFaceGuideline: Dispatch<SetStateAction<boolean>>;
  showPhotoOptions: boolean;
  setShowPhotoOptions: Dispatch<SetStateAction<boolean>>;

  // Handlers
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleCameraCapture: (file: File) => Promise<void>;
  handlePhotoConsentAccept: () => Promise<void>;
  requestCameraPermission: () => Promise<boolean>;

  // Utility
  logUserAction: (userId: string, actionType: string) => Promise<void>;
}

/**
 * Hook for managing photo analysis workflow
 * Handles photo consent, camera permissions, and image capture
 */
export const usePhotoAnalysis = ({
  masterProfile,
  setMasterProfile,
}: UsePhotoAnalysisOptions): UsePhotoAnalysisReturn => {
  const { t } = useTranslation('modals');
  
  // Photo state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [shouldAutoSend, setShouldAutoSend] = useState<boolean>(false);
  
  // Analysis provider selection (default to GPT-5.2 Vision for best accuracy)
  const [analysisProvider, setAnalysisProvider] = useState<AnalysisProvider>(DEFAULT_ANALYSIS_PROVIDER);

  // Modal state
  const [showPhotoConsent, setShowPhotoConsent] = useState<boolean>(false);
  const [showFaceGuideline, setShowFaceGuideline] = useState<boolean>(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState<boolean>(false);

  /**
   * Log user action to Firestore
   */
  const logUserAction = useCallback(async (userId: string, actionType: string): Promise<void> => {
    try {
      // Phase 1 Step 4: write only to masterProfiles
      const profileDoc = doc(db, 'masterProfiles', userId);
      await setDoc(profileDoc, {
        meta: { [actionType]: increment(1) },
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user actions:', error);
    }
  }, []);

  /**
   * Request camera permission
   * @returns true if permission granted, false otherwise
   */
  const requestCameraPermission = useCallback(async (): Promise<boolean> => {
    const { isIOS } = detectPlatform();
    
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('Camera API not available on this device/browser');
        alert(t('faceCapture.notSupported'));
        return false;
      }
      
      // Check for browser support of the Permissions API
      // Note: iOS (both Safari and Chrome) does NOT support navigator.permissions.query for camera
      // So we skip the permission check on iOS and go directly to getUserMedia
      if (!isIOS && navigator.permissions) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });

          if (permissionStatus.state === 'denied') {
            const guidanceKey = getPermissionGuidanceKey();
            alert(`${t('faceCapture.permissionRequired')}\n\n${t(`faceCapture.${guidanceKey}`)}`);
            return false;
          }

          if (permissionStatus.state === 'prompt') {
            console.log('Camera permission has not been granted yet. Prompting user...');
          }
        } catch (permErr) {
          // Permissions API not supported for camera - continue anyway
          console.log('Permissions API not available for camera, proceeding with getUserMedia');
        }
      }

      // Request camera access
      console.log('Requesting camera access via getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (stream) {
        console.log('Camera access granted');
        stream.getTracks().forEach((track) => track.stop()); // Stop camera immediately after check
        return true;
      }
    } catch (error) {
      console.error('Camera permission denied or unavailable:', error);
      // Show platform-specific guidance when permission is denied
      const guidanceKey = getPermissionGuidanceKey();
      alert(`${t('faceCapture.permissionRequired')}\n\n${t(`faceCapture.${guidanceKey}`)}`);
      return false;
    }
    return false;
  }, [t, getPermissionGuidanceKey]);

  /**
   * Handle photo consent acceptance
   * Saves consent to Firebase and shows face guideline modal
   */
  const handlePhotoConsentAccept = useCallback(async (): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const timestamp = new Date().toISOString();
      const updatedProfile: MasterProfile = {
        ...masterProfile,
        consent: {
          ...masterProfile.consent,
          photoAnalysisAccepted: true,
          photoConsentTimestamp: timestamp,
        },
      };

      setMasterProfile(updatedProfile);
      const profileDocRef = doc(db, 'masterProfiles', user.uid);
      await setDoc(
        profileDocRef,
        {
          consent: {
            photoAnalysisAccepted: true,
            photoConsentTimestamp: timestamp,
          },
        },
        { merge: true }
      );

      setShowPhotoConsent(false);
      console.log('✅ Photo consent saved');

      // Show face guideline modal instead of triggering file input
      setShowFaceGuideline(true);
    } catch (error) {
      console.error('❌ Error saving photo consent:', error);
    }
  }, [masterProfile, setMasterProfile]);

  /**
   * Handle camera capture from PhotoUploadOptions component
   * Provider is now global state, not passed per-capture
   */
  const handleCameraCapture = useCallback(async (file: File): Promise<void> => {
    setSelectedImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      const previewUrl = reader.result as string;
      setImagePreviewUrl(previewUrl);
      // Trigger auto-send via useEffect in App after state updates
      setShouldAutoSend(true);
    };
    reader.readAsDataURL(file);

    if (auth.currentUser) {
      await logUserAction(auth.currentUser.uid, 'cameraUploads');
    }
  }, [logUserAction]);

  /**
   * Handle image file selection from file input
   */
  const handleImageChange = useCallback(async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    // Check for photo consent first
    if (!masterProfile.consent?.photoAnalysisAccepted) {
      setShowPhotoConsent(true);
      e.target.value = ''; // Clear the file input
      return;
    }

    const isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Type assertion for capture attribute (non-standard but used on mobile)
      const inputElement = e.target as HTMLInputElement & { capture?: string };
      const isCameraSelected = isIPhone && inputElement.capture === 'camera';

      if (isCameraSelected) {
        const hasPermission = await requestCameraPermission();

        if (!hasPermission) {
          alert(
            'To use the camera, you need to enable permissions in Settings > Privacy > Camera.'
          );
          return;
        }
      }

      setSelectedImage(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result as string);
        // Trigger auto-send via useEffect after state updates
        setShouldAutoSend(true);
      };
      reader.readAsDataURL(file);

      if (auth.currentUser) {
        await logUserAction(auth.currentUser.uid, 'cameraUploads');
      }
    }
  }, [masterProfile.consent?.photoAnalysisAccepted, requestCameraPermission, logUserAction]);

  /**
   * Clear photo state after sending
   */
  const clearPhotoState = useCallback((): void => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setShouldAutoSend(false);
  }, []);

  /**
   * Reset auto-send flag (called by App's useEffect)
   */
  const resetAutoSend = useCallback((): void => {
    setShouldAutoSend(false);
  }, []);

  return {
    // Photo state
    selectedImage,
    setSelectedImage,
    imagePreviewUrl,
    setImagePreviewUrl,
    shouldAutoSend,
    resetAutoSend,
    clearPhotoState,
    
    // Analysis provider
    analysisProvider,
    setAnalysisProvider,

    // Modal state
    showPhotoConsent,
    setShowPhotoConsent,
    showFaceGuideline,
    setShowFaceGuideline,
    showPhotoOptions,
    setShowPhotoOptions,

    // Handlers
    handleImageChange,
    handleCameraCapture,
    handlePhotoConsentAccept,
    requestCameraPermission,

    // Utility
    logUserAction,
  };
};
